import { format, parseISO } from 'date-fns';
import type {
  CommissionModel,
  FormErrors,
  PayPeriod,
  PaymentFormValues,
  PaymentType,
} from '../types/payment';
import { formatAmountInput, hasAmount, parseAmount } from './currency';

export const emptyValues: PaymentFormValues = {
  loanId: null,
  grossCommission: '',
  flatFee: '',
  processingFee: '',
  netCommission: '',
  loSplit: '',
  loAmount: '',
  payrollAmount: '',
  balanceToPL: '',
  referralAmount: '',
  managerNetCommission: '',
  managerSplit: '',
  corpFee: '',
  recruitingBonus: '',
  expenseAmount: '',
  expenseCategory: '',
  expenseDetail: '',
  expenseDate: '',
};

export const loanRequiredTypes: PaymentType[] = [
  'payment',
  'referral',
  'manager_override',
  'recruiting_bonus',
];

export interface Visibility {
  loan: boolean;
  commissionDetails: boolean;
  /** Flat Fee model only. */
  flatFeeField: boolean;
  /** Flat Fee model only. */
  processingFeeField: boolean;
  /** Standard model only — replaces the fee fields in Commission Details. */
  commissionCorpFee: boolean;
  splitDetails: boolean;
  referral: boolean;
  managerOverride: boolean;
  recruitingBonus: boolean;
  expense: boolean;
  other: boolean;
}

export function getVisibility(
  paymentType: PaymentType | null,
  payPeriod: PayPeriod,
): Visibility {
  const isStandard = payPeriod.commissionModel === 'standard';
  const showsCommission =
    paymentType !== null && loanRequiredTypes.includes(paymentType);
  return {
    loan: showsCommission,
    commissionDetails: showsCommission,
    flatFeeField: showsCommission && !isStandard,
    processingFeeField: showsCommission && !isStandard,
    commissionCorpFee: showsCommission && isStandard,
    splitDetails: paymentType === 'payment' && isStandard,
    referral: paymentType === 'referral',
    managerOverride: paymentType === 'manager_override',
    recruitingBonus: paymentType === 'recruiting_bonus',
    expense: paymentType === 'expense',
    other: paymentType === 'other',
  };
}

export function validate(
  values: PaymentFormValues,
  paymentType: PaymentType,
  payPeriod: PayPeriod,
): FormErrors {
  const errors: FormErrors = {};
  const v = getVisibility(paymentType, payPeriod);

  if (v.loan && !values.loanId) {
    errors.loanId = 'Select the loan this payment belongs to.';
  }

  if (paymentType === 'payment' && !hasAmount(values.netCommission)) {
    errors.netCommission = 'Enter a net commission amount.';
  }

  if (paymentType === 'other' && !hasAmount(values.payrollAmount)) {
    errors.payrollAmount = 'Enter the payroll amount for this adjustment.';
  }

  if (paymentType === 'referral' && !hasAmount(values.referralAmount)) {
    errors.referralAmount = 'Enter the referral payment amount.';
  }

  if (paymentType === 'manager_override') {
    if (!hasAmount(values.managerNetCommission)) {
      errors.managerNetCommission = 'Enter the manager net commission.';
    }
    if (values.managerSplit && parseAmount(values.managerSplit) > 100) {
      errors.managerSplit = 'Split cannot exceed 100%.';
    }
  }

  if (paymentType === 'recruiting_bonus' && !hasAmount(values.recruitingBonus)) {
    errors.recruitingBonus = 'Enter the recruiting bonus amount.';
  }

  if (v.splitDetails && values.loSplit && parseAmount(values.loSplit) > 100) {
    errors.loSplit = 'LO Split cannot exceed 100%.';
  }

  if (paymentType === 'expense') {
    if (!hasAmount(values.expenseAmount)) {
      errors.expenseAmount = 'Enter the expense amount.';
    }
    if (!values.expenseCategory) {
      errors.expenseCategory = 'Choose a category so this maps to the P&L.';
    }
    if (!values.expenseDetail.trim()) {
      errors.expenseDetail = 'Add a short description of the expense.';
    }
    if (!values.expenseDate) {
      errors.expenseDate = 'Enter the date the expense was incurred.';
    }
  }

  if (paymentType === 'other' && !values.expenseDetail.trim()) {
    errors.expenseDetail = 'Describe this adjustment.';
  }

  return errors;
}

/** Fields that are always calculated unless the user types over them. */
export const derivedFields = [
  'netCommission',
  'loAmount',
  'payrollAmount',
  'balanceToPL',
  'managerNetCommission',
] as const;

/**
 * Net Commission formula by commission model:
 *  - Standard Model: Gross Commission − Corp Fee
 *  - Flat Fee Model: Gross Commission − Flat Fee − Processing Fee
 */
export function netCommissionDeductions(
  values: PaymentFormValues,
  corpFeeReducesNet: boolean,
): string[] {
  return corpFeeReducesNet
    ? [values.corpFee]
    : [values.flatFee, values.processingFee];
}

export function netCommissionFormulaLabel(corpFeeReducesNet: boolean): string {
  return corpFeeReducesNet
    ? 'Gross Commission − Corp Fee'
    : 'Gross Commission − Flat Fee − Processing Fee';
}

/** Auto-derives dependent currency fields the user has not manually edited. */
export function deriveValues(
  values: PaymentFormValues,
  manual: Set<string>,
  options: { corpFeeReducesNet?: boolean } = {},
): PaymentFormValues {
  const next = { ...values };
  const deductions = netCommissionDeductions(
    values,
    Boolean(options.corpFeeReducesNet),
  );
  const touchedInputs =
    hasAmount(values.grossCommission) || deductions.some((value) => hasAmount(value));

  if (!manual.has('netCommission') && touchedInputs) {
    const net = deductions.reduce(
      (total, value) => total - parseAmount(value),
      parseAmount(values.grossCommission),
    );
    next.netCommission = formatAmountInput(net.toFixed(2));
  }

  const net = parseAmount(next.netCommission);

  if (!manual.has('loAmount') && hasAmount(values.loSplit) && net !== 0) {
    next.loAmount = formatAmountInput(
      ((net * parseAmount(values.loSplit)) / 100).toFixed(2),
    );
  }
  if (
    !manual.has('managerNetCommission') &&
    hasAmount(values.managerSplit) &&
    net !== 0
  ) {
    next.managerNetCommission = formatAmountInput(
      ((net * parseAmount(values.managerSplit)) / 100).toFixed(2),
    );
  }
  if (!manual.has('payrollAmount') && hasAmount(next.loAmount)) {
    next.payrollAmount = formatAmountInput(next.loAmount);
  }
  if (!manual.has('balanceToPL') && hasAmount(next.loAmount)) {
    next.balanceToPL = formatAmountInput(
      (net - parseAmount(next.loAmount)).toFixed(2),
    );
  }

  return next;
}

export function getPrimaryAmount(
  values: PaymentFormValues,
  paymentType: PaymentType,
): number {
  switch (paymentType) {
    case 'referral':
      return parseAmount(values.referralAmount);
    case 'manager_override':
      return parseAmount(values.managerNetCommission);
    case 'recruiting_bonus':
      return parseAmount(values.recruitingBonus);
    case 'expense':
      return parseAmount(values.expenseAmount);
    case 'other':
      return parseAmount(values.payrollAmount);
    default:
      return parseAmount(values.netCommission);
  }
}

export function formatDateRange(start: string, end: string): string {
  const from = parseISO(start);
  const to = parseISO(end);
  const sameMonth = from.getMonth() === to.getMonth();
  return `${format(from, 'MMM d')} – ${format(to, sameMonth ? 'd, yyyy' : 'MMM d, yyyy')}`;
}

export function formatDate(value: string): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
}

export const commissionModelLabel: Record<CommissionModel, string> = {
  flat_fee: 'Flat Fee Model',
  standard: 'Standard Model',
  unsupported: 'Unsupported Model',
};
